import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, MapPin, Phone, Mail, Clock } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useSettings } from "@/lib/settings";

// Custom TikTok glyph
function TikTok({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.86 1.62V7a4.85 4.85 0 0 1-1.93-.31z"/>
        </svg>
    );
}

export default function SiteFooter() {
    const { settings } = useSettings();
    const s = settings || {};
    const year = new Date().getFullYear();

    return (
        <footer className="relative mt-16 border-t border-white/10 bg-black/70" data-testid="site-footer">
            <div className="heat-band absolute -top-px left-0 right-0 h-px" />
            <div className="container-narrow grid grid-cols-1 md:grid-cols-4 gap-8 py-12">
                <div>
                    <Link to="/" className="flex items-center gap-3 logo-glow" data-testid="footer-logo-link">
                        <BrandLogo size={56} />
                        <div>
                            <div className="font-display text-xl tracking-[0.06em]">SMOKIN&rsquo; MO&rsquo;S</div>
                            <div className="text-[10px] uppercase tracking-[0.32em] text-orange-300/80">Southern BBQ</div>
                        </div>
                    </Link>
                    <p className="mt-4 text-white/70 text-sm leading-relaxed">
                        Wharton, NJ native. Georgia-trained pitmaster. Best Food Truck Atlanta 2015. Back home and firing up Sandyston, NJ.
                    </p>
                </div>

                <div>
                    <div className="font-display text-lg tracking-[0.06em] mb-3 text-white">Visit / Call</div>
                    <ul className="text-sm text-white/80 space-y-2">
                        <li className="flex items-start gap-2" data-testid="footer-address">
                            <MapPin size={16} className="mt-0.5 text-orange-300" /><span>{s.address || "15 Route 206 S, Sandyston, NJ 07826"}</span>
                        </li>
                        <li className="flex items-start gap-2" data-testid="footer-phone">
                            <Phone size={16} className="mt-0.5 text-orange-300" />
                            <a href={`tel:${(s.phone || '8623032001').replace(/\D/g, '')}`} className="hover:text-orange-300" data-testid="footer-phone-link">{s.phone || "862-303-2001"}</a>
                        </li>
                        <li className="flex items-start gap-2" data-testid="footer-email">
                            <Mail size={16} className="mt-0.5 text-orange-300" />
                            <a href={`mailto:${s.email || 'smokinmosnj@gmail.com'}`} className="hover:text-orange-300" data-testid="footer-email-link">{s.email || "smokinmosnj@gmail.com"}</a>
                        </li>
                        <li className="flex items-start gap-2" data-testid="footer-hours">
                            <Clock size={16} className="mt-0.5 text-orange-300" /><span>{s.hours || "Thursday – Sunday · 11am – 7pm"}</span>
                        </li>
                    </ul>
                </div>

                <div>
                    <div className="font-display text-lg tracking-[0.06em] mb-3 text-white">Explore</div>
                    <ul className="text-sm text-white/80 space-y-2">
                        <li><Link to="/menu" className="hover:text-orange-300" data-testid="footer-link-menu">Menu</Link></li>
                        <li><Link to="/events" className="hover:text-orange-300" data-testid="footer-link-events">Events</Link></li>
                        <li><Link to="/gallery" className="hover:text-orange-300" data-testid="footer-link-gallery">Gallery</Link></li>
                        <li><Link to="/about" className="hover:text-orange-300" data-testid="footer-link-about">About</Link></li>
                        <li><Link to="/contact" className="hover:text-orange-300" data-testid="footer-link-contact">Book Catering</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="font-display text-lg tracking-[0.06em] mb-3 text-white">Follow</div>
                    <div className="flex items-center gap-3">
                        {s.instagram_url ? (
                            <a aria-label="Instagram" href={s.instagram_url} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-orange-400/50 hover:text-orange-300" data-testid="social-instagram">
                                <Instagram size={18} />
                            </a>
                        ) : null}
                        {s.facebook_url ? (
                            <a aria-label="Facebook" href={s.facebook_url} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-orange-400/50 hover:text-orange-300" data-testid="social-facebook">
                                <Facebook size={18} />
                            </a>
                        ) : null}
                        <a aria-label="TikTok" href={s.tiktok_url || "#"} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-orange-400/50 hover:text-orange-300" data-testid="social-tiktok">
                            <TikTok size={18} />
                        </a>
                        <a aria-label="YouTube" href={s.youtube_url || "#"} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-orange-400/50 hover:text-orange-300" data-testid="social-youtube">
                            <Youtube size={18} />
                        </a>
                    </div>
                    <p className="mt-4 text-xs text-white/50">Add your TikTok / YouTube links from the admin dashboard.</p>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="container-narrow py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
                    <span>&copy; {year} Smokin&rsquo; Mo&rsquo;s Southern BBQ. All rights reserved.</span>
                    <span>&nbsp;</span>
                </div>
            </div>
        </footer>
    );
}
